import {
  Actor,
  Component,
  Entity,
  Logger,
  PointerEvent,
  Query,
  Scene,
  Subscription,
} from "excalibur";
import { hasTouchSupport } from "../utils/mobile";

export type TargetableHudInfo = {
  id: string;
  name: string;
  portrait: string;
};

export class TargetableComponent extends Component {
  constructor(
    public id: number,
    public name: string,
    public portrait: string,
  ) {
    super();
  }
}

interface HasTargetManager {
  targetManager: TargetManager;
}

export function hasTargetManager(u: unknown): u is HasTargetManager {
  return (u as HasTargetManager).targetManager != null;
}

export type TargetFilter = (entity: Entity<TargetableComponent>) => boolean;

export class TargetManager {
  private _currentTarget: Entity<TargetableComponent> | undefined;
  private _hoverTarget: Entity<TargetableComponent> | undefined;
  private _targetFilter: TargetFilter | undefined;
  private _stickyHoverTarget: Entity<TargetableComponent> | undefined;

  query: Query<typeof TargetableComponent>;
  private _logger = Logger.getInstance();

  public set targetFilter(fn: TargetFilter | undefined) {
    this._targetFilter = fn;
    if (this.currentTarget && (!fn || !fn(this.currentTarget))) {
      this._currentTarget = undefined;
    }
  }

  public get currentTarget(): Entity<TargetableComponent> | undefined {
    return this._currentTarget;
  }

  public get hoverTarget(): Entity<TargetableComponent> | undefined {
    return this._stickyHoverTarget || this._hoverTarget;
  }

  pointerEnter(e: Entity<TargetableComponent>) {
    if (this._currentTarget != e) {
      this._hoverTarget = e;
    }
  }

  pointerLeave(e: Entity<TargetableComponent>) {
    if (this._hoverTarget == e) {
      this._hoverTarget = undefined;
    }
  }

  toggleStickyHover(e: Entity<TargetableComponent>) {
    if (this._stickyHoverTarget === e) {
      this._stickyHoverTarget = undefined;
    } else {
      this._stickyHoverTarget = e;
    }
  }

  clearStickyHover() {
    this._stickyHoverTarget = undefined;
  }

  pointerDown(e: Entity<TargetableComponent>) {
    // Handle touch sticky hover first, regardless of targeting
    if (hasTouchSupport()) {
      this.toggleStickyHover(e);
    }

    // Then handle targeting logic
    if (!this._targetFilter || !this._targetFilter(e)) return;
    this._currentTarget = e;
  }

  _subscriptions: Map<number, Subscription[]> = new Map();
  constructor(public scene: Scene) {
    this.query = this.scene.world.query([TargetableComponent]);

    this.query.entityAdded$.subscribe((e) => {
      const targetComponent = e.get(TargetableComponent);
      this._logger.info("entity Addded", e.name, targetComponent.id);
      this._subscriptions.set(e.id, [
        (e as Actor).on("pointerdown", (evt: PointerEvent) => {
          // Only cancel on desktop to prevent drag selection
          if (!hasTouchSupport()) {
            evt.cancel();
          }
          this.pointerDown(e);
        }),
        (e as Actor).on("pointerenter", () => this.pointerEnter(e)),
        (e as Actor).on("pointerleave", () => this.pointerLeave(e)),
      ] as const);
    });

    this.query.entityRemoved$.subscribe((e) => {
      if (this._currentTarget == e) this._currentTarget = undefined;
      if (this._hoverTarget == e) this._hoverTarget = undefined;
      if (this._stickyHoverTarget == e) this._stickyHoverTarget = undefined;

      const subs = this._subscriptions.get(e.id);
      if (!subs) return;
      for (const { close } of subs) {
        close();
      }
    });
  }
}
